// Infer a source-code / registry URL for a service from its image ref.
//
//   ghcr.io/<org>/<repo>:tag  → https://github.com/<org>/<repo>
//   <org>/<repo>:tag          → https://hub.docker.com/r/<org>/<repo>
//   <name>:tag                → https://hub.docker.com/_/<name>
//
// Returns null when the input is empty. The kind discriminator lets callers
// swap the icon (GitHub vs Docker) without re-parsing.

export type ImageSource = {
  href: string
  kind: 'github' | 'dockerhub'
}

// Hardcoded org for repo_name override. Matches the backend's
// FetchLatestCommit which also targets github.com/pentarab/<repo>.
const DEFAULT_GITHUB_ORG = 'pentarab'

export function sourceURL(image: string): ImageSource | null {
  if (!image) return null
  const bare = image.replace(/:[^:/]+$/, '')
  const gh = bare.match(/^ghcr\.io\/([^/]+)\/(.+)$/)
  if (gh) return { href: `https://github.com/${gh[1]}/${gh[2]}`, kind: 'github' }
  if (bare.includes('/')) {
    return { href: `https://hub.docker.com/r/${bare}`, kind: 'dockerhub' }
  }
  return { href: `https://hub.docker.com/_/${bare}`, kind: 'dockerhub' }
}

// serviceSourceURL prefers the explicit repo_name override when set (lets
// operators point the icon at a repo that doesn't match the image's last
// segment) and falls back to sourceURL(image) otherwise. Accepts an
// optional full github URL in repo_name so users can paste a cross-org
// link too.
export function serviceSourceURL(svc: {
  image: string
  repo_name?: string
}): ImageSource | null {
  const repo = (svc.repo_name ?? '').trim()
  if (repo) {
    // Accept either a bare repo ("aso-saas") or a full URL
    // ("https://github.com/pentarab/aso-saas" / "github.com/org/repo").
    if (/^https?:\/\//i.test(repo)) {
      return { href: repo, kind: 'github' }
    }
    if (repo.startsWith('github.com/')) {
      return { href: `https://${repo}`, kind: 'github' }
    }
    // Slash-containing form like "other-org/some-repo" overrides the
    // default org; plain names inherit DEFAULT_GITHUB_ORG.
    const path = repo.includes('/') ? repo : `${DEFAULT_GITHUB_ORG}/${repo}`
    return { href: `https://github.com/${path}`, kind: 'github' }
  }
  return sourceURL(svc.image)
}
